import RealtimeKitClient from "@cloudflare/realtimekit";
import { defineCustomElements } from "@cloudflare/realtimekit-ui/loader";
import { defaultLanguage, useLanguage } from "@cloudflare/realtimekit-ui";
import { resolveVideoDefault, type CallType } from "./call-type";

// Registers <rtk-meeting> and all nested UI Kit custom elements. Safe to call
// once at module load; defineCustomElements() no-ops on repeat registration.
defineCustomElements();

// Every FeatHub-facing surface is in Spanish (Chile); RealtimeKit's UI Kit
// defaults to English with no locale auto-detection. Only overriding the
// strings a host/guest actually sees in this minimal room UI (no polls/
// livestream/breakout strings — unused here).
const es = useLanguage({
  ...defaultLanguage,
  join: "Unirse",
  leave: "Salir",
  cancel: "Cancelar",
  leave_confirmation: "¿Seguro que quieres salir de la llamada?",
  "end.all": "Finalizar reunión para todos",
  mic_on: "Micrófono activado",
  mic_off: "Micrófono apagado",
  video_on: "Cámara activada",
  video_off: "Cámara apagada",
  mute: "Silenciar",
  mute_all: "Silenciar a todos",
  participants: "Participantes",
  chat: "Chat",
  settings: "Configuración",
  polls: "Encuestas",
  screenshare: "Compartir pantalla",
  "screenshare.start": "Compartir pantalla",
  "screenshare.stop": "Dejar de compartir",
  "recording.start": "Iniciar grabación",
  "recording.stop": "Detener grabación",
  end: "Finalizar reunión",
  "setup_screen.join_in_as": "Ingresar como",
  "setup_screen.your_name": "Tu nombre",
  "network.reconnecting": "Se perdió la conexión. Reconectando...",
  "network.restored": "Conexión restablecida",
  ended: "La reunión terminó.",
  "ended.left": "Saliste de la reunión.",
  "ended.kicked": "Fuiste removido de la reunión.",
  "ended.rejected": "El anfitrión rechazó tu solicitud para unirte.",
  "waitlist.body_text": "Estás en la sala de espera, el anfitrión te dejará entrar en breve.",
});

/**
 * Initializes a RealtimeKit meeting session and mounts the UI Kit's
 * <rtk-meeting> web component into `container`. Used identically by both the
 * host flow and the (post-admission) guest flow.
 *
 * `callType` (issue #8) only decides whether video starts active
 * (`defaults.video`) — it never touches `defaults.audio`, nor any
 * RealtimeKit preset/permission. The UI Kit's camera control stays visible
 * and functional in audio-only mode; the participant can turn it on anytime.
 */
export async function mountMeeting(
  authToken: string,
  callType: CallType,
  container: HTMLElement,
): Promise<void> {
  // Presets already target "hd" quality server-side, but without an explicit
  // capture resolution here getUserMedia falls back to a conservative default
  // (observed: noticeably blurry on a real 3-way call) — no preset can add
  // back detail that was never captured. 720p/24fps matches what the docs
  // recommend for small group calls (cloudflare.com/realtime/realtimekit/
  // best-practices/video-and-simulcast).
  const meeting = await RealtimeKitClient.init({
    authToken,
    defaults: {
      video: resolveVideoDefault(callType),
      audio: true,
      mediaConfiguration: {
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } },
      },
    },
  });

  container.innerHTML = "";
  const meetingEl = document.createElement("rtk-meeting");
  // `meeting` is a complex object and must be set as a JS property, not an
  // HTML attribute (per the UI Kit's Web Components usage docs).
  (meetingEl as unknown as { meeting: typeof meeting }).meeting = meeting;
  (meetingEl as unknown as { mode: string }).mode = "fill";
  (meetingEl as unknown as { showSetupScreen: boolean }).showSetupScreen = true;
  (meetingEl as unknown as { t: typeof es }).t = es;
  container.appendChild(meetingEl);
}
